import { Tag, TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';

import TestimonialCardFooter from './TestimonialCardFooter';
import type {
  LandingPageTestimonial,
  LandingPageTestimonialsTheme,
} from './types';
import getCardClasses from './utils/getCardClasses';

export interface MobileTestimonialCardProps
  extends Omit<LandingPageTestimonial, 'id'> {
  readonly inverted: boolean;
  readonly theme: LandingPageTestimonialsTheme;
}

const MobileTestimonialCard = ({
  action,
  company,
  image,
  inverted,
  name,
  nameMsgKey,
  quote,
  tag,
  theme,
}: MobileTestimonialCardProps) => {
  const t = useTranslations();
  const hasFooter = !!nameMsgKey || !!name || !!company || !!action;

  return (
    <div className="relative flex h-full w-[75vw] flex-col overflow-hidden rounded-md">
      <div className="relative aspect-square w-full">
        <Image
          className="object-cover"
          data-cy="landing-page-testimonials-mobile-card-img"
          fill
          localizedAlt={image.alt}
          sizes="75vw"
          src={image.src}
        />
      </div>
      <div
        className={cx(
          'flex w-full flex-1 flex-col justify-between p-6',
          getCardClasses({ inverted, theme }),
        )}
        data-cy="landing-page-testimonials-mobile-card-content"
      >
        <div>
          <Tag variant={theme}>{t(tag)}</Tag>
        </div>
        <blockquote>
          <TypographyV3
            className="my-4 text-body-lg leading-tight before:content-[open-quote] after:content-[close-quote]"
            Element="p"
            color="inherit"
            font="serif"
            weight="thin"
          >
            <RichText messageKey={quote} />
          </TypographyV3>
          {hasFooter && (
            <footer>
              <TestimonialCardFooter
                action={action}
                company={company}
                name={name}
                nameMsgKey={nameMsgKey}
              />
            </footer>
          )}
        </blockquote>
      </div>
    </div>
  );
};

export default MobileTestimonialCard;
